import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import WizardFrame from './WizardFrame';
import ConnectionAvatar from './ConnectionAvatar';
import { INTEGRATIONS_LOGO_PATH } from '../common/constants';
import {
  getXeroTenants,
  getXeroTenantOptions,
  saveXeroTenantMapping,
  disconnectXeroTenant,
  startOAuth
} from '../../utils/api_integrations';

// ---------------------------------------------------------------- #
// XeroWizard
// ---------------------------------------------------------------- #
//
// The Xero wizard (plan 05-xero.md):
//   1. Connect: the entity's Xero organisations with their parks,
//      "Connect a Xero organisation", and per organisation "Map parks"
//      (which opens step 2 on that organisation) and Disconnect.
//   2. Map parks: the organisation being mapped (a dropdown to switch when
//      there are several), "Importing from Xero" until its tracking
//      categories and budgets have been imported, then one row per park:
//      linked, method, category, options, budgets, unallocated. The mapping
//      rules are checked as you go.
//   3. Review: what will change, any rule problems, and Save (the only
//      step that writes). Save sends the whole organisation: a park left
//      unticked is unlinked from it.
//
// Props (the same contract as every provider wizard):
//   register       The `data` of GET register
//   parkiId        The park the wizard was opened from, or null
//   initialStep    The step to open at (2, Map parks, after returning from Xero)
//   connectionKey  The bdoXeroId (as a string) to open on, or null
//   canConnect     False while staff are viewing as the customer
//   onClose        Called with true when something was saved, false otherwise

// ---------------------------------------------------------------- #
// Constants
// ---------------------------------------------------------------- #

/** The three step labels. */
const XERO_STEPS = ['Connect', 'Map parks', 'Review'];

/** Step numbers, named so the code reads plainly. */
const STEP_CONNECT = 1;
const STEP_MAP = 2;
const STEP_REVIEW = 3;

/** Gap in pixels between a locked pill and the tooltip's caret. */
const PILL_TOOLTIP_GAP = 8;

/** The two stored mapping methods. */
const METHOD_WHOLE = 'wholeTenancy';
const METHOD_TRACKING = 'trackingCategory';

/** What the user sees for each mapping method. */
const METHOD_LABELS = {
  [METHOD_WHOLE]: 'Whole file',
  [METHOD_TRACKING]: 'Tracking category'
};

/** Message shown on the Connect button while staff are viewing as the customer. */
const IMPERSONATING_MESSAGE = 'Ask the customer to connect their own account';

/** Shown while the Batch import has not yet filled the tracking categories and budgets. */
const IMPORTING_MESSAGE = 'Importing from Xero. This usually takes a few minutes; you can leave this page.';

/** How often to check again while the import is running, in milliseconds (30 seconds). */
const IMPORT_POLL_INTERVAL_MS = 30000;

/** Label for a park whose register rows Parki support has to sort out. */
const CONTACT_SUPPORT_LABEL = 'Contact support';

/** Label for a park whose plan does not include bookkeeping. */
const NOT_IN_PLAN_LABEL = 'Not in plan';

/** Reason shown for a linked park that is not in the admin's park list; its link is kept as it is. */
const OUTSIDE_PLAN_REASON = 'This park is not in your current park list, so its link is kept as it is.';


// ---------------------------------------------------------------- #
// Helpers: choices
// ---------------------------------------------------------------- #

/**
 * Leave out organisations that were disconnected: switched off with no parks linked, ours or anyone else's.
 * One that is switched off but still has parks stays, so someone can see and fix it.
 */
const keepConnectedTenants = (tenants) => {
  const kept = [];
  for (const tenant of tenants) {
    const ownParkCount = (tenant.parks || []).length;
    const otherParkCount = tenant.otherParkCount || 0;
    const hasParks = ownParkCount + otherParkCount > 0;
    const isDisconnected = tenant.isActive !== true && !hasParks;
    if (isDisconnected) {
      continue;
    }
    kept.push(tenant);
  }
  return kept;
};

/**
 * A park's choice when it is not linked to the organisation.
 */
const buildEmptyChoice = () => {
  return {
    linked: false,
    integrationMethod: METHOD_WHOLE,
    trackingCategory: '',
    trackingOptions: [],
    selectedBudgets: [],
    unallocatedTransactions: false
  };
};

/**
 * Turn a list of ids into strings, so comparisons do not trip over numbers.
 */
const toStringList = (values) => {
  const strings = [];
  for (const value of values || []) {
    strings.push(String(value));
  }
  return strings;
};

/**
 * True when two id lists hold the same ids, in any order.
 */
const sameIdList = (first, second) => {
  // Lists of different lengths cannot match
  if (first.length !== second.length) {
    return false;
  }

  // Every id in the first list must be in the second
  for (const id of first) {
    if (!second.includes(id)) {
      return false;
    }
  }
  return true;
};

/**
 * True when two park choices would save the same thing.
 */
const choicesMatch = (first, second) => {
  // Linked or not decides first
  if (first.linked !== second.linked) {
    return false;
  }

  // Two unlinked parks are the same whatever their other fields say
  if (!first.linked) {
    return true;
  }

  // Compare every saved field
  const sameMethod = first.integrationMethod === second.integrationMethod;
  const sameCategory = first.trackingCategory === second.trackingCategory;
  const sameOptions = sameIdList(first.trackingOptions, second.trackingOptions);
  const sameBudgets = sameIdList(first.selectedBudgets, second.selectedBudgets);
  const sameUnallocated = first.unallocatedTransactions === second.unallocatedTransactions;
  return sameMethod && sameCategory && sameOptions && sameBudgets && sameUnallocated;
};

/**
 * Find one organisation in the list by its bdoXeroId, or null.
 */
const findTenant = (tenants, bdoXeroId) => {
  if (!bdoXeroId) {
    return null;
  }
  for (const tenant of tenants || []) {
    if (String(tenant.bdoXeroId) === String(bdoXeroId)) {
      return tenant;
    }
  }
  return null;
};

/**
 * Pick the organisation to open on: the one asked for, else the one the park uses, else the only one.
 */
const pickInitialTenantId = (tenants, connectionKey, parkiId) => {
  // The organisation the page asked for (a warning's Fix, or a return from Xero)
  const askedFor = findTenant(tenants, connectionKey);
  if (askedFor) {
    return String(askedFor.bdoXeroId);
  }

  // The first organisation the opened park is linked to
  const parkGiven = parkiId !== null && parkiId !== undefined;
  if (parkGiven) {
    for (const tenant of tenants) {
      for (const park of tenant.parks || []) {
        if (String(park.parkiId) === String(parkiId)) {
          return String(tenant.bdoXeroId);
        }
      }
    }
  }

  // With only one organisation there is nothing to choose
  if (tenants.length === 1) {
    return String(tenants[0].bdoXeroId);
  }
  return null;
};

/**
 * Build the rows of the map table: every park in the register, plus any park linked to this organisation that is not.
 */
const buildRows = (register, tenant) => {
  const rows = [];
  const seenParkKeys = {};

  // One row per park in the register
  for (const park of register?.parks || []) {
    const parkKey = String(park.parkiId);
    const cell = park.cells?.bookkeeping || {};

    // Note the MYOB company files this park already uses (one bookkeeping provider per park)
    const myobNames = [];
    for (const link of cell.mapping?.links || []) {
      if (link.provider === 'myob') {
        myobNames.push(link.connectionName);
      }
    }

    // Parks the register will not let us change are shown but locked
    let locked = false;
    let lockedReason = null;
    if (cell.editable !== true) {
      locked = true;
      lockedReason = NOT_IN_PLAN_LABEL;
      if (cell.state === 'contact-support') {
        lockedReason = CONTACT_SUPPORT_LABEL;
      }
      if (cell.reason) {
        lockedReason = `${lockedReason}: ${cell.reason}`;
      }
    }

    rows.push({
      parkiId: park.parkiId,
      parkKey: parkKey,
      parkName: park.parkName,
      cell: cell,
      locked: locked,
      lockedReason: lockedReason,
      myobNames: myobNames
    });
    seenParkKeys[parkKey] = true;
  }

  // A park linked here but missing from the register keeps its link, locked, so a save does not unlink it
  for (const tenantPark of tenant?.parks || []) {
    const parkKey = String(tenantPark.parkiId);
    if (seenParkKeys[parkKey]) {
      continue;
    }
    rows.push({
      parkiId: tenantPark.parkiId,
      parkKey: parkKey,
      parkName: tenantPark.parkName,
      cell: {},
      locked: true,
      lockedReason: OUTSIDE_PLAN_REASON,
      myobNames: []
    });
  }

  return rows;
};

/**
 * Read each park's stored settings on one organisation into the choices the form edits.
 */
const buildChoicesForTenant = (rows, tenant) => {
  // Every row starts unlinked
  const choices = {};
  for (const row of rows) {
    choices[row.parkKey] = buildEmptyChoice();
  }

  // Fill in the parks the organisation already links
  for (const tenantPark of tenant?.parks || []) {
    const parkKey = String(tenantPark.parkiId);
    let method = METHOD_TRACKING;
    if (tenantPark.integrationMethod === METHOD_WHOLE) {
      method = METHOD_WHOLE;
    }
    choices[parkKey] = {
      linked: true,
      integrationMethod: method,
      trackingCategory: String(tenantPark.trackingCategory || ''),
      trackingOptions: toStringList(tenantPark.trackingOptions),
      selectedBudgets: toStringList(tenantPark.selectedBudgets),
      unallocatedTransactions: tenantPark.unallocatedTransactions === true
    };
  }

  return choices;
};

/**
 * The method a newly ticked park starts with: whole organisation when it is alone, otherwise tracking category.
 */
const pickDefaultMethod = (choices, parkKey) => {
  // Look for any other park already ticked
  for (const otherKey of Object.keys(choices)) {
    const otherIsLinked = otherKey !== parkKey && choices[otherKey].linked;
    if (otherIsLinked) {
      return METHOD_TRACKING;
    }
  }
  return METHOD_WHOLE;
};

// ---------------------------------------------------------------- #
// Helpers: tracking categories and budgets
// ---------------------------------------------------------------- #

/**
 * Find a tracking category by id in the imported options, or null.
 */
const findCategory = (options, categoryId) => {
  for (const category of options?.trackingCategories || []) {
    if (String(category.trackingCategoryId) === String(categoryId)) {
      return category;
    }
  }
  return null;
};

/**
 * The name of a tracking category, or its id when it is not in the imported list.
 */
const describeCategory = (options, categoryId) => {
  const category = findCategory(options, categoryId);
  if (category) {
    return category.trackingCategoryName;
  }
  return `${categoryId} (no longer in Xero)`;
};

/**
 * The names of some tracking options in a category, joined with commas.
 */
const describeOptions = (options, categoryId, optionIds) => {
  // Look each id up in the category's options
  const category = findCategory(options, categoryId);
  const names = [];
  for (const optionId of optionIds) {
    let name = `${optionId} (no longer in Xero)`;
    for (const option of category?.options || []) {
      if (String(option.trackingOptionId) === optionId) {
        name = option.trackingOptionName;
      }
    }
    names.push(name);
  }
  return names.join(', ');
};

/**
 * The names of some budgets, joined with commas.
 */
const describeBudgets = (options, budgetIds) => {
  // Look each id up in the imported budgets
  const names = [];
  for (const budgetId of budgetIds) {
    let name = `${budgetId} (no longer in Xero)`;
    for (const budget of options?.budgets || []) {
      if (String(budget.budgetId) === budgetId) {
        name = budget.budgetDescription || budget.budgetType;
      }
    }
    names.push(name);
  }
  return names.join(', ');
};

/**
 * Describe one linked park's settings as one line for the review list.
 */
const describeSettings = (choice, options) => {
  const parts = [];

  // The method, and for tracking the category and options when any are picked
  const isWhole = choice.integrationMethod === METHOD_WHOLE;
  const hasOptions = choice.trackingOptions.length > 0;
  if (isWhole) {
    parts.push('Whole file');
  }
  if (!isWhole && hasOptions) {
    const categoryName = describeCategory(options, choice.trackingCategory);
    const optionNames = describeOptions(options, choice.trackingCategory, choice.trackingOptions);
    parts.push(`Tracking category ${categoryName}: ${optionNames}`);
  }

  // The unallocated flag: on its own it is the only thing the park gets
  if (!isWhole && choice.unallocatedTransactions && hasOptions) {
    parts.push('Also gets transactions with no tracking option');
  }
  if (!isWhole && choice.unallocatedTransactions && !hasOptions) {
    parts.push('Only transactions with no tracking option');
  }

  // Neither: say so, so the review shows what is missing
  if (!isWhole && !choice.unallocatedTransactions && !hasOptions) {
    parts.push('By tracking category, no options picked');
  }

  // The budgets, when any are picked
  if (choice.selectedBudgets.length > 0) {
    parts.push(`Budgets: ${describeBudgets(options, choice.selectedBudgets)}`);
  }

  return parts.join('. ');
};

// ---------------------------------------------------------------- #
// Helpers: the mapping rules
// ---------------------------------------------------------------- #

/**
 * Check the whole organisation against the mapping rules; returns one problem per broken rule per park.
 */
const checkMappingRules = (rows, choices, options, tenantName) => {
  const problems = [];

  // Collect the rows that are linked
  const linkedRows = [];
  for (const row of rows) {
    const choice = choices[row.parkKey];
    if (!choice || !choice.linked) {
      continue;
    }
    linkedRows.push(row);
  }
  const moreThanOnePark = linkedRows.length > 1;

  // Rule: a whole-organisation park is the only park on the organisation
  for (const row of linkedRows) {
    const isWhole = choices[row.parkKey].integrationMethod === METHOD_WHOLE;
    if (isWhole && moreThanOnePark) {
      problems.push({
        parkiId: row.parkiId,
        message: `${row.parkName} is set to whole file, so it must be the only park on ${tenantName}. Use a tracking category, or unlink the other parks.`
      });
    }
  }

  // Collect the parks that receive unallocated transactions
  const unallocatedRows = [];
  for (const row of linkedRows) {
    if (choices[row.parkKey].unallocatedTransactions) {
      unallocatedRows.push(row);
    }
  }

  // Rule: at most one park receives unallocated transactions
  if (unallocatedRows.length > 1) {
    const unallocatedNames = [];
    for (const row of unallocatedRows) {
      unallocatedNames.push(row.parkName);
    }
    for (const row of unallocatedRows) {
      problems.push({
        parkiId: row.parkiId,
        message: `Only one park on ${tenantName} can receive unallocated transactions, but ${unallocatedNames.join(' and ')} are all ticked.`
      });
    }
  }

  // Rule: only a tracking category park can receive unallocated transactions
  for (const row of unallocatedRows) {
    const isWhole = choices[row.parkKey].integrationMethod === METHOD_WHOLE;
    if (isWhole) {
      problems.push({
        parkiId: row.parkiId,
        message: `${row.parkName} uses the whole file, so it cannot also receive transactions with no tracking option. Only a tracking category park can.`
      });
    }
  }

  // Only check ids against Xero's lists once they have been imported
  const listsImported = Boolean(options) && options.optionsImported === true;

  // Rule: a tracking park takes something from this organisation: options from one category, or the transactions with no tracking option
  for (const row of linkedRows) {
    const choice = choices[row.parkKey];
    if (choice.integrationMethod !== METHOD_TRACKING) {
      continue;
    }

    // An option, or the "transactions with no tracking option" tick, must be picked
    const hasOptions = choice.trackingOptions.length > 0;
    const takesUntrackedTransactions = choice.unallocatedTransactions === true;
    if (!hasOptions && !takesUntrackedTransactions) {
      problems.push({ parkiId: row.parkiId, message: `${row.parkName}: pick at least one tracking option, or tick "Also gets transactions with no tracking option".` });
      continue;
    }

    // Untracked transactions only: no category or options to check
    if (!hasOptions) {
      continue;
    }

    // Options need their category
    if (!choice.trackingCategory) {
      problems.push({ parkiId: row.parkiId, message: `${row.parkName}: pick a tracking category.` });
      continue;
    }

    // The category must still be in Xero
    if (!listsImported) {
      continue;
    }
    const category = findCategory(options, choice.trackingCategory);
    if (!category) {
      problems.push({ parkiId: row.parkiId, message: `${row.parkName}: its tracking category is no longer in ${tenantName}. Pick again.` });
      continue;
    }

    // Every option must belong to that category
    const categoryOptionIds = [];
    for (const option of category.options || []) {
      categoryOptionIds.push(String(option.trackingOptionId));
    }
    for (const optionId of choice.trackingOptions) {
      if (!categoryOptionIds.includes(optionId)) {
        problems.push({ parkiId: row.parkiId, message: `${row.parkName}: one of its tracking options is not in ${category.trackingCategoryName}. Pick again.` });
        break;
      }
    }
  }

  // Rule: budgets come from this organisation
  if (listsImported) {
    const budgetIds = [];
    for (const budget of options.budgets || []) {
      budgetIds.push(String(budget.budgetId));
    }
    for (const row of linkedRows) {
      for (const budgetId of choices[row.parkKey].selectedBudgets) {
        if (!budgetIds.includes(budgetId)) {
          problems.push({ parkiId: row.parkiId, message: `${row.parkName}: one of its budgets is no longer in ${tenantName}. Pick again.` });
          break;
        }
      }
    }
  }

  // Rule: one bookkeeping provider per park
  for (const row of linkedRows) {
    if (row.myobNames.length > 0) {
      problems.push({ parkiId: row.parkiId, message: `${row.parkName} is already set up with MYOB. Remove it from its MYOB company files first.` });
    }
  }

  return problems;
};

// ---------------------------------------------------------------- #
// The wizard
// ---------------------------------------------------------------- #

/**
 * The four-step Xero wizard.
 */
const XeroWizard = ({ register, parkiId = null, initialStep = 1, connectionKey = null, canConnect = true, onClose }) => {

  // ---------------------------------------------------------------- #
  // State
  // ---------------------------------------------------------------- #

  const [step, setStep] = useState(initialStep);
  const [maxReachedStep, setMaxReachedStep] = useState(initialStep);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState(null);
  const [options, setOptions] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsReloadCount, setOptionsReloadCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [changed, setChanged] = useState(false);
  const [savedChoices, setSavedChoices] = useState({});
  const [choices, setChoices] = useState({});
  const [apiProblems, setApiProblems] = useState([]);
  const [saveSummary, setSaveSummary] = useState(null);
  const [parkFilter, setParkFilter] = useState('');
  const [activeParkKey, setActiveParkKey] = useState(null);

  // The tooltip over a locked pill, naming the park that uses it, or null when hidden
  const [pillTip, setPillTip] = useState(null);

  // The opened park is ticked for the first organisation only, so it is not ticked again after a save
  const preselectDone = useRef(false);

  // The opened park's row, so the Map parks step can scroll to it
  const openedParkRow = useRef(null);

  // The organisation being mapped, and the rows of its map table
  const selectedTenant = findTenant(tenants, tenantId);
  const tenantName = selectedTenant?.tenantName || 'this file';
  const rows = buildRows(register, selectedTenant);

  // ---------------------------------------------------------------- #
  // Choosing the organisation
  // ---------------------------------------------------------------- #

  /**
   * Make an organisation the one being mapped, and read its parks into the form.
   */
  const applyTenant = (newTenantId, tenantList) => {
    // Read the stored settings into the form
    const tenant = findTenant(tenantList, newTenantId);
    const tenantRows = buildRows(register, tenant);
    const storedChoices = buildChoicesForTenant(tenantRows, tenant);
    const formChoices = { ...storedChoices };

    // Tick the opened park when it has no bookkeeping link anywhere yet
    const shouldPreselect = Boolean(tenant) && parkiId !== null && !preselectDone.current;
    if (shouldPreselect) {
      preselectDone.current = true;
      for (const row of tenantRows) {
        const isOpenedPark = String(row.parkiId) === String(parkiId);
        const existingLinks = row.cell.mapping?.links || [];
        const canPreselect = isOpenedPark && !row.locked && existingLinks.length === 0;
        if (!canPreselect) {
          continue;
        }
        const method = pickDefaultMethod(storedChoices, row.parkKey);
        formChoices[row.parkKey] = { ...buildEmptyChoice(), linked: true, integrationMethod: method };
      }
    }

    // Show the organisation, and clear what belonged to the last one
    setTenantId(newTenantId);
    setSavedChoices(storedChoices);
    setChoices(formChoices);
    setOptions(null);
    setApiProblems([]);
    setSaveSummary(null);
  };

  // ---------------------------------------------------------------- #
  // Load the organisations
  // ---------------------------------------------------------------- #

  /**
   * Read the entity's Xero organisations again; returns the list.
   */
  const loadTenants = async () => {
    const result = await getXeroTenants();
    const tenantList = keepConnectedTenants(result.data.tenants || []);
    setTenants(tenantList);
    return tenantList;
  };

  // Read the organisations when the wizard opens, and pick the one to map
  useEffect(() => {
    let cancelled = false;
    const loadOnOpen = async () => {
      try {
        const result = await getXeroTenants();
        if (cancelled) {
          return;
        }
        const tenantList = keepConnectedTenants(result.data.tenants || []);
        setTenants(tenantList);

        // Open on the organisation the page asked for, or the obvious one
        const initialTenantId = pickInitialTenantId(tenantList, connectionKey, parkiId);
        if (initialTenantId) {
          applyTenant(initialTenantId, tenantList);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Your Xero files could not be loaded.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadOnOpen();

    return () => {
      cancelled = true;
    };
    // Runs once on open; the props it reads do not change while the wizard is open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------- #
  // Load the tracking categories and budgets
  // ---------------------------------------------------------------- #

  // Read the chosen organisation's categories and budgets, and again on every "check again"
  useEffect(() => {
    if (!tenantId) {
      return;
    }

    let cancelled = false;
    const loadOptions = async () => {
      setOptionsLoading(true);
      try {
        const result = await getXeroTenantOptions(tenantId);
        if (!cancelled) {
          setOptions(result.data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'The tracking categories could not be loaded.');
        }
      } finally {
        if (!cancelled) {
          setOptionsLoading(false);
        }
      }
    };
    loadOptions();

    return () => {
      cancelled = true;
    };
  }, [tenantId, optionsReloadCount]);

  // While the import is still running, check again every 30 seconds
  useEffect(() => {
    const stillImporting = Boolean(options) && options.optionsImported !== true;
    if (!stillImporting) {
      return;
    }
    const timer = setTimeout(() => {
      setOptionsReloadCount(previous => previous + 1);
    }, IMPORT_POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [options]);

  // ---------------------------------------------------------------- #
  // Step navigation
  // ---------------------------------------------------------------- #

  /**
   * Show a step and remember it as reached.
   */
  const goToStep = (stepNumber) => {
    setError(null);
    setNotice(null);
    setStep(stepNumber);
    if (stepNumber > maxReachedStep) {
      setMaxReachedStep(stepNumber);
    }
  };

  // Hide the pill tooltip when anything scrolls, so it never floats away from its pill
  useEffect(() => {
    if (!pillTip) {
      return;
    }
    const hideOnScroll = () => {
      setPillTip(null);
    };
    window.addEventListener('scroll', hideOnScroll, true);
    return () => {
      window.removeEventListener('scroll', hideOnScroll, true);
    };
  }, [pillTip]);

  // Bring the opened park's row into view when the map step opens
  useEffect(() => {
    const onMapStep = step === STEP_MAP;
    if (onMapStep && openedParkRow.current) {
      openedParkRow.current.scrollIntoView({ block: 'center' });
    }
  }, [step]);

  // ---------------------------------------------------------------- #
  // Changes and the rules
  // ---------------------------------------------------------------- #

  /**
   * List every park whose choice differs from what is saved.
   */
  const buildChanges = () => {
    const changes = [];
    for (const row of rows) {
      const before = savedChoices[row.parkKey] || buildEmptyChoice();
      const after = choices[row.parkKey] || buildEmptyChoice();
      if (choicesMatch(before, after)) {
        continue;
      }

      // Say what kind of change it is
      let kind = 'changed';
      if (!before.linked) {
        kind = 'linked';
      } else if (!after.linked) {
        kind = 'unlinked';
      }
      changes.push({ row: row, before: before, after: after, kind: kind });
    }
    return changes;
  };

  /**
   * True when there are choices that have not been saved.
   */
  function isDirty() {
    return buildChanges().length > 0;
  }

  // The rule problems with the form as it stands
  const ruleProblems = checkMappingRules(rows, choices, options, tenantName);

  /**
   * The rule problems for one park, as a list of messages.
   */
  const problemsForPark = (rowParkiId) => {
    const messages = [];
    for (const problem of ruleProblems) {
      if (String(problem.parkiId) === String(rowParkiId)) {
        messages.push(problem.message);
      }
    }
    return messages;
  };

  // ---------------------------------------------------------------- #
  // Step 1: connect and disconnect
  // ---------------------------------------------------------------- #

  /**
   * Send the browser to Xero's sign-in.
   */
  const handleConnect = async () => {
    // Leaving the app loses unsaved choices, so ask first
    if (isDirty()) {
      const userAgreed = window.confirm('Discard changes?');
      if (!userAgreed) {
        return;
      }
    }

    setBusy(true);
    setError(null);
    try {
      // Ask the API for the sign-in address and go there
      const result = await startOAuth('xero', parkiId);
      window.location.href = result.data.authoriseUrl;
    } catch (err) {
      setError(err.message || 'Xero sign-in could not be started.');
      setBusy(false);
    }
  };

  /**
   * Disconnect one organisation at Xero and unlink its parks, after the user confirms.
   */
  const handleDisconnect = async (tenant) => {
    // List the parks that will lose their Xero data
    const parkNames = [];
    for (const park of tenant.parks || []) {
      parkNames.push(park.parkName);
    }
    let affectedText = 'No parks are linked to it.';
    if (parkNames.length > 0) {
      affectedText = `These parks will stop receiving Xero data: ${parkNames.join(', ')}.`;
    }

    // Confirm before disconnecting
    const userAgreed = window.confirm(`Disconnect ${tenant.tenantName} from Parki? ${affectedText}`);
    if (!userAgreed) {
      return;
    }

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      // Disconnect, then read the organisations again
      const result = await disconnectXeroTenant(tenant.bdoXeroId);
      const tenantList = await loadTenants();
      setChanged(true);
      setNotice(result.data.message || `Disconnected ${tenant.tenantName}.`);

      // The organisation being mapped now has no parks, so read it into the form again
      const wasSelected = String(tenant.bdoXeroId) === String(tenantId);
      if (wasSelected) {
        applyTenant(String(tenant.bdoXeroId), tenantList);
      }
    } catch (err) {
      setError(err.message || 'The file could not be disconnected.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Jump from step 1 straight to mapping one organisation.
   */
  const handleMapTenant = (tenant) => {
    const newTenantId = String(tenant.bdoXeroId);
    const isDifferent = newTenantId !== String(tenantId);

    // Switching organisation throws away unsaved choices, so ask first
    if (isDifferent && isDirty()) {
      const userAgreed = window.confirm('Discard changes?');
      if (!userAgreed) {
        return;
      }
    }

    // Read it into the form and go straight to mapping its parks
    if (isDifferent) {
      applyTenant(newTenantId, tenants);
    }
    goToStep(STEP_MAP);
  };

  // ---------------------------------------------------------------- #
  // Step 2: switch organisation
  // ---------------------------------------------------------------- #

  /**
   * Switch the Map parks step to a different organisation.
   */
  const handlePickTenant = (newTenantId) => {
    // Nothing to do when it is already picked
    if (newTenantId === String(tenantId)) {
      return;
    }

    // Switching throws away unsaved choices, so ask first
    if (isDirty()) {
      const userAgreed = window.confirm('Discard changes?');
      if (!userAgreed) {
        return;
      }
    }
    applyTenant(newTenantId, tenants);
  };

  /**
   * Check again whether the import has finished.
   */
  const handleCheckImport = () => {
    setOptionsReloadCount(previous => previous + 1);
  };

  // ---------------------------------------------------------------- #
  // Step 2: edit the choices
  // ---------------------------------------------------------------- #

  /**
   * Replace some fields of one park's choice.
   */
  const updateChoice = (parkKey, fields) => {
    // A new edit means the last save result no longer describes the screen
    setSaveSummary(null);
    setApiProblems([]);

    setChoices(previous => {
      const parkChoice = { ...(previous[parkKey] || buildEmptyChoice()), ...fields };
      return { ...previous, [parkKey]: parkChoice };
    });
  };

  /**
   * Tick or untick a park as linked to this organisation.
   */
  const handleLinkedChange = (parkKey, linked) => {
    // Unlinking clears the park's settings
    if (!linked) {
      updateChoice(parkKey, buildEmptyChoice());
      return;
    }

    // Linking starts with the method that fits how many parks are ticked
    const method = pickDefaultMethod(choices, parkKey);
    updateChoice(parkKey, { ...buildEmptyChoice(), linked: true, integrationMethod: method });
  };

  /**
   * Change a park's method; whole organisation drops the category, options and unallocated flag.
   */
  const handleMethodChange = (parkKey, method) => {
    if (method === METHOD_WHOLE) {
      updateChoice(parkKey, {
        integrationMethod: METHOD_WHOLE,
        trackingCategory: '',
        trackingOptions: [],
        unallocatedTransactions: false
      });
      return;
    }
    updateChoice(parkKey, { integrationMethod: METHOD_TRACKING });
  };

  /**
   * Change a park's tracking category; the options of the old category no longer apply.
   */
  const handleCategoryChange = (parkKey, categoryId) => {
    updateChoice(parkKey, { trackingCategory: categoryId, trackingOptions: [] });
  };

  /**
   * Tick or untick one id in one of a park's lists (options or budgets).
   */
  const handleListTick = (parkKey, field, id, ticked) => {
    // Start from the current list without this id
    const currentList = choices[parkKey]?.[field] || [];
    const newList = [];
    for (const existingId of currentList) {
      if (existingId !== id) {
        newList.push(existingId);
      }
    }

    // Add the id back when it is ticked
    if (ticked) {
      newList.push(id);
    }
    updateChoice(parkKey, { [field]: newList });
  };

  /**
   * Make one park the one that receives unallocated transactions, or nobody when parkKey is null.
   */
  const handleUnallocatedChange = (parkKey) => {
    setSaveSummary(null);
    setApiProblems([]);

    // Exactly one park, or none, keeps the flag
    setChoices(previous => {
      const next = {};
      for (const key of Object.keys(previous)) {
        const isChosen = key === parkKey;
        next[key] = { ...previous[key], unallocatedTransactions: isChosen };
      }
      return next;
    });
  };

  // ---------------------------------------------------------------- #
  // Step 3: save
  // ---------------------------------------------------------------- #

  /**
   * Save every park linked to the organisation; parks left unticked are unlinked.
   */
  const handleSave = async () => {
    // Build one entry per linked park, with every key filled
    const parksToSave = {};
    for (const row of rows) {
      const choice = choices[row.parkKey];
      if (!choice || !choice.linked) {
        continue;
      }

      // Whole organisation parks send an empty category and option list
      const isWhole = choice.integrationMethod === METHOD_WHOLE;
      let trackingCategory = choice.trackingCategory;
      let trackingOptions = choice.trackingOptions;
      if (isWhole) {
        trackingCategory = '';
        trackingOptions = [];
      }
      parksToSave[row.parkKey] = {
        integrationMethod: choice.integrationMethod,
        trackingCategory: trackingCategory,
        trackingOptions: trackingOptions,
        selectedBudgets: choice.selectedBudgets,
        unallocatedTransactions: choice.unallocatedTransactions === true
      };
    }

    setBusy(true);
    setError(null);
    setApiProblems([]);
    try {
      // Save the whole organisation
      const result = await saveXeroTenantMapping(selectedTenant.bdoXeroId, parksToSave);
      setChanged(true);

      // Count the parks the register would not let us change
      const statusChanges = result.data.statusChanges || [];
      let skippedCount = 0;
      for (const statusChange of statusChanges) {
        if (statusChange.skippedBecause) {
          skippedCount += 1;
        }
      }

      // The current choices are now the saved ones
      const unlinkedParkiIds = result.data.unlinkedParkiIds || [];
      setSavedChoices(choices);
      setSaveSummary({
        linkedCount: Object.keys(parksToSave).length,
        unlinkedCount: unlinkedParkiIds.length,
        skippedCount: skippedCount
      });

      // Read the organisations again so step 1 shows the new links
      await loadTenants();
    } catch (err) {
      setError(err.message || 'The changes could not be saved.');
      const returnedProblems = err.data?.problems;
      if (Array.isArray(returnedProblems)) {
        setApiProblems(returnedProblems);
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * After a save, go back to the Map parks step so another organisation can be picked from its dropdown.
   */
  const handleMapAnother = () => {
    setSaveSummary(null);
    goToStep(STEP_MAP);
  };

  /**
   * Close the wizard, telling the page whether anything was saved.
   */
  const handleClose = () => {
    onClose(changed);
  };

  // ---------------------------------------------------------------- #
  // Render: shared pieces
  // ---------------------------------------------------------------- #

  /**
   * A centred spinner with optional text.
   */
  const renderSpinner = (text = null) => {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary mb-2" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        {text && <div>{text}</div>}
      </div>
    );
  };

  /**
   * The names of an organisation's parks, joined with commas, or a note when there are none.
   */
  const describeTenantParks = (tenant) => {
    const parkNames = [];
    for (const park of tenant.parks || []) {
      parkNames.push(park.parkName);
    }
    if (parkNames.length === 0) {
      return 'No parks linked yet';
    }
    return parkNames.join(', ');
  };

  // ---------------------------------------------------------------- #
  // Render: step 1
  // ---------------------------------------------------------------- #

  /**
   * Draw the connected organisations and the Connect button.
   */
  const renderConnectStep = () => {
    if (loading) {
      return renderSpinner();
    }

    // Staff viewing as the customer see a locked button; connecting is the customer's job
    let connectLabel = 'Connect a Xero file';
    if (tenants.length > 0) {
      connectLabel = 'Connect another file';
    }
    let connectButton = (
      <button type="button" className="btn btn-primary" onClick={handleConnect} disabled={busy}>
        <i className="icon-base ti tabler-plug-connected me-1"></i>
        {connectLabel}
      </button>
    );
    if (!canConnect) {
      connectButton = (
        <button type="button" className="btn btn-label-secondary" disabled title={IMPERSONATING_MESSAGE}>
          <i className="icon-base ti tabler-lock me-1"></i>
          Customer connects this
        </button>
      );
    }

    return (
      <>
        {notice && (
          <div className="alert alert-success" role="status">{notice}</div>
        )}

        {/* Not connected: one heading, one line, one button */}
        {tenants.length === 0 && (
          <div className="integrations-connect-hero">
            <h4>Connect Xero</h4>
            <p>Tick one file on Xero's screen (Xero calls it an organisation). Connect again for each other file.</p>
            {connectButton}
          </div>
        )}

        {/* Connected: one compact card per organisation, then the button to add another */}
        {tenants.length > 0 && (
          <>
            {tenants.map(tenant => (
              <div key={tenant.bdoXeroId} className="integrations-connection-card mb-3">
                <ConnectionAvatar name={tenant.tenantName} />
                <div className="integrations-connection-card-text">
                  <div className="fw-medium">{tenant.tenantName}</div>
                  <div className="text-body-secondary small">{describeTenantParks(tenant)}</div>
                  {tenant.otherParkCount > 0 && (
                    <div className="text-body-secondary small">Also linked to {tenant.otherParkCount} park(s) outside your park list.</div>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-sm btn-label-primary" onClick={() => handleMapTenant(tenant)} disabled={busy}>
                    Map parks
                  </button>
                  <button type="button" className="btn btn-sm btn-label-danger" onClick={() => handleDisconnect(tenant)} disabled={busy}>
                    Disconnect
                  </button>
                </div>
              </div>
            ))}
            <div className="text-center">
              {connectButton}
            </div>
          </>
        )}
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Render: step 2, the organisation at the top
  // ---------------------------------------------------------------- #

  /**
   * Draw the top of the Map parks step: which organisation is being mapped (a dropdown when
   * there are several), and whether Xero is still importing its tracking categories and budgets.
   */
  const renderOrganisationBar = () => {
    // Work out the import state of the organisation being mapped
    const stillImporting = Boolean(options) && options.optionsImported !== true;
    const firstLoad = optionsLoading && !options;
    const hasSeveralTenants = tenants.length > 1;
    const showPicker = hasSeveralTenants || !selectedTenant;

    // The dropdown's choices: one per organisation, with a prompt when none is picked yet
    const tenantOptions = [];
    for (const tenant of tenants) {
      tenantOptions.push(
        <option key={tenant.bdoXeroId} value={String(tenant.bdoXeroId)}>{tenant.tenantName}</option>
      );
    }
    let pickedValue = '';
    if (selectedTenant) {
      pickedValue = String(selectedTenant.bdoXeroId);
    }

    return (
      <>
        {/* Several organisations, or none picked yet: say which one, with a dropdown to switch */}
        {showPicker && (
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <label htmlFor="xero-tenant-picker" className="mb-0">Parks that use</label>
            <select
              id="xero-tenant-picker"
              className="form-select form-select-sm w-auto fw-medium"
              value={pickedValue}
              onChange={(event) => handlePickTenant(event.target.value)}
              disabled={busy}
            >
              {!selectedTenant && <option value="">Pick a file</option>}
              {tenantOptions}
            </select>
          </div>
        )}

        {/* One organisation: name it in the sentence */}
        {!showPicker && (
          <p>Parks that use <strong>{tenantName}</strong></p>
        )}

        {/* Still reading the organisation's categories and budgets */}
        {selectedTenant && firstLoad && (
          <p className="text-body-secondary small">
            <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
            Reading tracking categories...
          </p>
        )}

        {/* Xero has not finished importing them yet */}
        {selectedTenant && stillImporting && (
          <div className="alert alert-info d-flex flex-wrap align-items-center justify-content-between gap-2" role="status">
            <span>
              <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
              {IMPORTING_MESSAGE} You can link a park to the whole file now.
            </span>
            <button type="button" className="btn btn-sm btn-label-primary" onClick={handleCheckImport} disabled={optionsLoading}>
              Check again
            </button>
          </div>
        )}
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Render: step 2, the park list and the park's settings
  // ---------------------------------------------------------------- #

  /**
   * Work out the note, problems and lock state shared by a park's list item and its settings panel.
   */
  const describeParkRow = (row) => {
    const choice = choices[row.parkKey] || buildEmptyChoice();

    // A park on MYOB cannot be linked here, unless it already is (then the rule problem says so)
    const blockedByMyob = row.myobNames.length > 0 && !choice.linked;
    const linkDisabled = row.locked || blockedByMyob;

    // Pick the note shown about the park
    let parkNote = null;
    if (row.locked) {
      parkNote = row.lockedReason;
    } else if (blockedByMyob) {
      parkNote = `Set up with MYOB (${row.myobNames.join(', ')})`;
    }

    return {
      choice: choice,
      linkDisabled: linkDisabled,
      parkNote: parkNote,
      problems: problemsForPark(row.parkiId)
    };
  };

  /**
   * Show the tooltip above a locked pill. Pills that are free to pick have no tooltip.
   */
  const showPillTip = (event, text) => {
    if (!text) {
      return;
    }
    const pillBox = event.currentTarget.getBoundingClientRect();
    const left = pillBox.left + pillBox.width / 2;
    const top = pillBox.top - PILL_TOOLTIP_GAP;
    setPillTip({ text: text, left: left, top: top });
  };

  /**
   * Hide the pill tooltip.
   */
  const hidePillTip = () => {
    setPillTip(null);
  };

  /**
   * The name of another linked park that already uses this option or budget, or null when none does.
   */
  const findOtherParkUsing = (parkKey, field, itemId) => {
    for (const otherRow of rows) {
      const isThisPark = otherRow.parkKey === parkKey;
      if (isThisPark) {
        continue;
      }
      const otherChoice = choices[otherRow.parkKey];
      const otherIsLinked = Boolean(otherChoice) && otherChoice.linked === true;
      if (!otherIsLinked) {
        continue;
      }

      // A whole-file park does not use tracking categories, even if old settings still list some
      const otherUsesSplit = otherChoice.integrationMethod === METHOD_TRACKING;
      if (!otherUsesSplit) {
        continue;
      }
      const otherUsesItem = otherChoice[field].includes(itemId);
      if (otherUsesItem) {
        return otherRow.parkName;
      }
    }
    return null;
  };

  /**
   * Draw a row of clickable pills, one per option or budget; a pill is on when the park uses it.
   * With oneParkOnly, a pill another park already uses is disabled here until that park lets it go.
   */
  const renderPills = (row, field, items, emptyText, groupLabel, oneParkOnly) => {
    const choice = choices[row.parkKey];

    // Nothing to pick yet
    if (items.length === 0) {
      return <span className="text-body-secondary small">{emptyText}</span>;
    }

    return (
      <div className="integrations-pills" role="group" aria-label={groupLabel}>
        {items.map(item => {
          const isOn = choice[field].includes(item.id);

          // Another park holds this one, so it is locked here (unless this park also has it, so it can be let go)
          let takenBy = null;
          if (oneParkOnly && !isOn) {
            takenBy = findOtherParkUsing(row.parkKey, field, item.id);
          }
          const isTaken = takenBy !== null;

          // The pill's look and its hover text
          let pillClass = 'integrations-pill';
          if (isOn) {
            pillClass += ' is-on';
          }
          if (isTaken) {
            pillClass += ' is-taken';
          }
          let hoverText = undefined;
          if (isTaken) {
            hoverText = `Used by ${takenBy}`;
          }

          return (
            <button
              key={item.id}
              type="button"
              className={pillClass}
              aria-pressed={isOn}
              aria-disabled={isTaken}
              onMouseEnter={(event) => showPillTip(event, hoverText)}
              onMouseLeave={hidePillTip}
              onFocus={(event) => showPillTip(event, hoverText)}
              onBlur={hidePillTip}
              onClick={() => {
                if (isTaken) {
                  return;
                }
                handleListTick(row.parkKey, field, item.id, !isOn);
              }}
            >
              {isOn && <i className="icon-base ti tabler-check" aria-hidden="true"></i>}
              {item.label}
              {isTaken && <span className="visually-hidden">, used by {takenBy}</span>}
            </button>
          );
        })}
      </div>
    );
  };

  /**
   * Draw one park in the left-hand list: an icon for its state, its name and a one-line summary.
   */
  const renderParkListItem = (row, isActive) => {
    const details = describeParkRow(row);
    const isOpenedPark = parkiId !== null && String(row.parkiId) === String(parkiId);
    const hasProblems = details.problems.length > 0;

    // The summary line: its settings when linked, else why it cannot be linked, else nothing
    let summary = '';
    if (details.choice.linked) {
      summary = describeSettings(details.choice, options);
    } else if (details.parkNote) {
      summary = details.parkNote;
    }

    // The icon: a warning when a rule is broken, a tick when linked, a dashed circle otherwise
    let iconClass = 'tabler-circle-dashed text-body-secondary';
    if (details.choice.linked) {
      iconClass = 'tabler-circle-check text-success';
    }
    if (hasProblems) {
      iconClass = 'tabler-alert-triangle text-danger';
    }

    // The selected park is highlighted
    let itemClass = 'integrations-mapper-item';
    if (isActive) {
      itemClass += ' is-active';
    }
    if (!details.choice.linked) {
      itemClass += ' is-unlinked';
    }

    // The park the wizard was opened from is scrolled into view
    let itemRef = null;
    if (isOpenedPark) {
      itemRef = openedParkRow;
    }

    return (
      <button
        key={row.parkKey}
        ref={itemRef}
        type="button"
        className={itemClass}
        aria-current={isActive}
        onClick={() => setActiveParkKey(row.parkKey)}
      >
        <i className={`icon-base ti ${iconClass} integrations-mapper-item-icon`} aria-hidden="true"></i>
        <span className="integrations-mapper-item-text">
          <span className="integrations-mapper-item-name">{row.parkName}</span>
          {summary !== '' && (
            <span className="integrations-mapper-item-summary">{summary}</span>
          )}
        </span>
      </button>
    );
  };

  /**
   * Draw the right-hand panel: the selected park's link switch and, when linked, its settings.
   */
  const renderParkPanel = (row) => {
    const details = describeParkRow(row);
    const choice = details.choice;
    const isTracking = choice.integrationMethod === METHOD_TRACKING;
    const switchId = `xero-linked-${row.parkKey}`;

    // The category select's choices
    const categories = options?.trackingCategories || [];

    // The options of the chosen category, as pill items
    const category = findCategory(options, choice.trackingCategory);
    const optionItems = [];
    for (const option of category?.options || []) {
      optionItems.push({ id: String(option.trackingOptionId), label: option.trackingOptionName });
    }

    // The budgets, as pill items
    const budgetItems = [];
    for (const budget of options?.budgets || []) {
      const budgetLabel = budget.budgetDescription || budget.budgetType;
      budgetItems.push({ id: String(budget.budgetId), label: budgetLabel });
    }

    // A category stored but no longer imported still needs an option to show
    const showMissingCategory = Boolean(choice.trackingCategory) && !category;

    // Which other park, if any, gets the untracked transactions now
    let otherUnallocatedName = null;
    for (const otherRow of rows) {
      const isThisPark = otherRow.parkKey === row.parkKey;
      const otherHasFlag = choices[otherRow.parkKey]?.unallocatedTransactions === true;
      if (!isThisPark && otherHasFlag) {
        otherUnallocatedName = otherRow.parkName;
      }
    }

    // Locked parks and unlinked parks have no settings to edit
    const showSettings = choice.linked && !row.locked;
    const showLockedSettings = choice.linked && row.locked;

    // Only one park per file can get the transactions with no tracking option: when another park has it, this tick is locked
    const thisParkHasUnallocated = choice.unallocatedTransactions === true;
    const unallocatedTakenElsewhere = otherUnallocatedName !== null && !thisParkHasUnallocated;
    let unallocatedBoxClass = 'form-check integrations-mapper-check mb-0';
    if (unallocatedTakenElsewhere) {
      unallocatedBoxClass += ' is-disabled';
    }

    // The method buttons: the chosen one is filled, the other outlined
    let wholeButtonClass = 'btn btn-sm btn-secondary';
    let trackingButtonClass = 'btn btn-sm btn-outline-secondary';
    if (isTracking) {
      wholeButtonClass = 'btn btn-sm btn-outline-secondary';
      trackingButtonClass = 'btn btn-sm btn-secondary';
    }

    /**
     * Give this park the untracked transactions, or take them away so no park gets them.
     */
    const handleUnallocatedTick = (ticked) => {
      if (ticked) {
        handleUnallocatedChange(row.parkKey);
        return;
      }
      handleUnallocatedChange(null);
    };

    return (
      <div className="integrations-mapper-panel">
        {/* The park's name and its link switch */}
        <div className="integrations-mapper-panel-head">
          <h5 className="mb-0">{row.parkName}</h5>
          <div className="form-check form-switch mb-0">
            <input
              id={switchId}
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={choice.linked}
              disabled={details.linkDisabled}
              onChange={(event) => handleLinkedChange(row.parkKey, event.target.checked)}
            />
            <label className="form-check-label" htmlFor={switchId}>Uses this file</label>
          </div>
        </div>

        {/* Why the park cannot be changed here */}
        {details.parkNote && (
          <p className="text-body-secondary small mb-0">{details.parkNote}</p>
        )}

        {/* Rule problems for this park */}
        {details.problems.length > 0 && (
          <div className="alert alert-danger mb-0" role="alert">
            {details.problems.map((message, index) => (
              <div key={index}>{message}</div>
            ))}
          </div>
        )}

        {/* Not linked: one line saying what the switch does */}
        {!choice.linked && !details.linkDisabled && (
          <p className="text-body-secondary mb-0">This park does not use {tenantName}. Switch on "Uses this file" to choose its transactions.</p>
        )}

        {/* Locked but linked: show the settings as text */}
        {showLockedSettings && (
          <p className="text-body-secondary mb-0">{describeSettings(choice, options)}</p>
        )}

        {/* Linked: method and category side by side */}
        {showSettings && (
          <div className="integrations-mapper-fields">
            <div className="integrations-mapper-field">
              <span className="integrations-mapper-label" id={`xero-method-${row.parkKey}`}>Which transactions</span>
              <div className="btn-group" role="group" aria-labelledby={`xero-method-${row.parkKey}`}>
                <button
                  type="button"
                  className={wholeButtonClass}
                  aria-pressed={!isTracking}
                  onClick={() => handleMethodChange(row.parkKey, METHOD_WHOLE)}
                >
                  {METHOD_LABELS[METHOD_WHOLE]}
                </button>
                <button
                  type="button"
                  className={trackingButtonClass}
                  aria-pressed={isTracking}
                  onClick={() => handleMethodChange(row.parkKey, METHOD_TRACKING)}
                >
                  {METHOD_LABELS[METHOD_TRACKING]}
                </button>
              </div>
            </div>

            {isTracking && (
              <div className="integrations-mapper-field">
                <label className="integrations-mapper-label" htmlFor={`xero-category-${row.parkKey}`}>Tracking category</label>
                <select
                  id={`xero-category-${row.parkKey}`}
                  className="form-select form-select-sm"
                  value={choice.trackingCategory}
                  onChange={(event) => handleCategoryChange(row.parkKey, event.target.value)}
                >
                  <option value="">Pick a category</option>
                  {showMissingCategory && (
                    <option value={choice.trackingCategory}>{`${choice.trackingCategory} (no longer in Xero)`}</option>
                  )}
                  {categories.map(item => (
                    <option key={item.trackingCategoryId} value={String(item.trackingCategoryId)}>{item.trackingCategoryName}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Linked by tracking category: the options this park uses */}
        {showSettings && isTracking && (
          <div className="integrations-mapper-field">
            <span className="integrations-mapper-label">Tracking options for this park</span>
            {renderPills(row, 'trackingOptions', optionItems, 'Pick a category first', 'Tracking options', true)}
          </div>
        )}

        {/* Linked: the budgets this park uses */}
        {showSettings && (
          <div className="integrations-mapper-field">
            <span className="integrations-mapper-label">Budgets</span>
            {renderPills(row, 'selectedBudgets', budgetItems, 'No budgets imported', 'Budgets', false)}
          </div>
        )}

        {/* Linked by tracking category: whether it also gets transactions with no tracking option */}
        {showSettings && isTracking && (
          <div className={unallocatedBoxClass}>
            <input
              id={`xero-unallocated-${row.parkKey}`}
              type="checkbox"
              className="form-check-input"
              checked={choice.unallocatedTransactions === true}
              disabled={unallocatedTakenElsewhere}
              onChange={(event) => handleUnallocatedTick(event.target.checked)}
            />
            <label className="form-check-label" htmlFor={`xero-unallocated-${row.parkKey}`}>
              Also gets transactions with no tracking option
              <span className="text-body-secondary">
                {unallocatedTakenElsewhere && ` (set for ${otherUnallocatedName}. Untick it there first.)`}
                {!unallocatedTakenElsewhere && ' (one park per file)'}
              </span>
            </label>
          </div>
        )}
      </div>
    );
  };

  /**
   * Draw the Map parks step: the file at the top, then the park list on the left and the selected park's settings on the right.
   */
  const renderMapStep = () => {
    if (loading) {
      return renderSpinner();
    }
    if (tenants.length === 0) {
      return <p className="mb-0">Connect a Xero file first.</p>;
    }

    // No file picked yet: show only the dropdown
    if (!selectedTenant) {
      return renderOrganisationBar();
    }

    // Keep the parks whose name matches the search, split into linked and not linked
    const filterText = parkFilter.trim().toLowerCase();
    const linkedRows = [];
    const unlinkedRows = [];
    for (const row of rows) {
      const parkName = (row.parkName || '').toLowerCase();
      const matches = filterText === '' || parkName.includes(filterText);
      if (!matches) {
        continue;
      }
      const isLinked = choices[row.parkKey]?.linked === true;
      if (isLinked) {
        linkedRows.push(row);
      } else {
        unlinkedRows.push(row);
      }
    }

    // The selected park: the one clicked, else the opened park, else the first linked park, else the first park
    let activeRow = null;
    for (const row of rows) {
      if (row.parkKey === activeParkKey) {
        activeRow = row;
      }
    }
    if (!activeRow) {
      for (const row of rows) {
        const isOpenedPark = parkiId !== null && String(row.parkiId) === String(parkiId);
        if (isOpenedPark) {
          activeRow = row;
        }
      }
    }
    if (!activeRow && linkedRows.length > 0) {
      activeRow = linkedRows[0];
    }
    if (!activeRow && rows.length > 0) {
      activeRow = rows[0];
    }
    const activeKey = activeRow ? activeRow.parkKey : null;

    return (
      <>
        {renderOrganisationBar()}

        <div className="integrations-mapper">
          {/* Left: search, then the linked parks, then the rest */}
          <nav className="integrations-mapper-list" aria-label="Parks">
            <div className="integrations-mapper-search">
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="Search parks"
                aria-label="Search parks"
                value={parkFilter}
                onChange={(event) => setParkFilter(event.target.value)}
              />
            </div>
            <div className="integrations-mapper-items">
              <div className="integrations-mapper-group">Linked ({linkedRows.length})</div>
              {linkedRows.map(row => renderParkListItem(row, row.parkKey === activeKey))}
              {linkedRows.length === 0 && (
                <div className="integrations-mapper-empty">No parks use this file yet.</div>
              )}
              <div className="integrations-mapper-group">Not linked ({unlinkedRows.length})</div>
              {unlinkedRows.map(row => renderParkListItem(row, row.parkKey === activeKey))}
            </div>
          </nav>

          {/* Right: the selected park */}
          {activeRow && renderParkPanel(activeRow)}
        </div>
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Render: step 3
  // ---------------------------------------------------------------- #

  /**
   * Draw the list of changes, the rule problems, and the result once saved.
   */
  const renderReviewStep = () => {
    // After saving, say what happened
    if (saveSummary) {
      let savedText = `Saved ${tenantName}: ${saveSummary.linkedCount} park(s) linked.`;
      if (saveSummary.unlinkedCount > 0) {
        savedText = `Saved ${tenantName}: ${saveSummary.linkedCount} park(s) linked, ${saveSummary.unlinkedCount} unlinked.`;
      }
      return (
        <>
          <div className="alert alert-success" role="status">{savedText}</div>
          {saveSummary.skippedCount > 0 && (
            <p className="text-body-secondary">{saveSummary.skippedCount} park(s) did not have their register status changed because they are not in your plan or need Parki support.</p>
          )}
        </>
      );
    }

    // List the changes park by park
    const changes = buildChanges();

    // Problems from the form, else the ones the API sent back
    let shownProblems = ruleProblems;
    if (shownProblems.length === 0) {
      shownProblems = apiProblems;
    }

    return (
      <>
        {changes.length === 0 && (
          <p>No changes to save.</p>
        )}
        {changes.length > 0 && (
          <>
            <p>These changes will be saved to <strong>{tenantName}</strong>:</p>
            <ul className="integrations-review-list">
              {changes.map(change => (
                <li key={change.row.parkKey}>
                  <strong>{change.row.parkName}</strong>
                  <ul>
                    {change.kind === 'linked' && (
                      <li>Linked: {describeSettings(change.after, options)}</li>
                    )}
                    {change.kind === 'unlinked' && (
                      <li>Unlinked. It stops receiving data from {tenantName}.</li>
                    )}
                    {change.kind === 'changed' && (
                      <>
                        <li>Was: {describeSettings(change.before, options)}</li>
                        <li>Now: {describeSettings(change.after, options)}</li>
                      </>
                    )}
                  </ul>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Rule problems, which block the save */}
        {shownProblems.length > 0 && (
          <div className="alert alert-danger" role="alert">
            <div className="fw-medium mb-1">Fix these on the Map parks step before saving:</div>
            <ul className="mb-0">
              {shownProblems.map((problem, index) => (
                <li key={index}>{problem.message}</li>
              ))}
            </ul>
          </div>
        )}
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Footer button for each step
  // ---------------------------------------------------------------- #

  // Next by default; Review from the map; Save on Review; Close once saved
  let primaryLabel = 'Next';
  let onPrimary = () => goToStep(step + 1);
  let primaryDisabled = false;
  let footerStart = null;
  if (step === STEP_CONNECT) {
    primaryDisabled = loading || tenants.length === 0;
  } else if (step === STEP_MAP) {
    primaryLabel = 'Review';
    primaryDisabled = !selectedTenant;
  } else if (step === STEP_REVIEW) {
    if (saveSummary) {
      primaryLabel = 'Close';
      onPrimary = handleClose;
      footerStart = (
        <button type="button" className="btn btn-sm btn-label-secondary" onClick={handleMapAnother}>
          Map another file
        </button>
      );
    } else {
      const noChanges = buildChanges().length === 0;
      const hasProblems = ruleProblems.length > 0;
      primaryLabel = 'Save';
      onPrimary = handleSave;
      primaryDisabled = noChanges || hasProblems || !selectedTenant;
    }
  }

  // Unsaved choices make closing ask first
  const dirty = isDirty();

  // ---------------------------------------------------------------- #
  // Render
  // ---------------------------------------------------------------- #

  return (
    <WizardFrame
      title="Xero"
      logoSrc={`${INTEGRATIONS_LOGO_PATH}/xero.png`}
      register={register}
      parkiId={parkiId}
      products={['bookkeeping']}
      onNotNeededChange={() => setChanged(true)}
      steps={XERO_STEPS}
      step={step}
      maxReachedStep={maxReachedStep}
      onStepChange={goToStep}
      error={error}
      dirty={dirty}
      onClose={handleClose}
      primaryLabel={primaryLabel}
      onPrimary={onPrimary}
      primaryDisabled={primaryDisabled}
      busy={busy}
      hideBack={Boolean(saveSummary)}
      footerStart={footerStart}
    >
      {step === STEP_CONNECT && renderConnectStep()}
      {step === STEP_MAP && renderMapStep()}
      {step === STEP_REVIEW && renderReviewStep()}

      {/* The tooltip over a locked pill, drawn on the page body: the modal's transform would otherwise offset a fixed position */}
      {pillTip && createPortal(
        <div className="integrations-tooltip" role="tooltip" style={{ left: pillTip.left, top: pillTip.top }}>
          <div className="integrations-tooltip-title">{pillTip.text}</div>
        </div>,
        document.body
      )}
    </WizardFrame>
  );
};

export default XeroWizard;
